import React, { useState, useCallback, useRef, useEffect } from "react";
import { Controller } from "react-hook-form";
import AsyncSelect from "react-select/async";
import { Col } from "react-bootstrap";
import { GetAllServiceCodeDescription } from "../config/preauthorizationApi";
import { useDispatch, useSelector } from "react-redux";
import { GetAllServicePriceList } from "features/price/config/PriceApi";

const ServiceCodeSelect = ({
  control,
  setValue,
  name,
  handleServiceMenuToggle,
  selectedServiceType,
  medication = false,
  isRequired,
  label,
  watch,
}) => {
  const dispatch = useDispatch();
  const { viewModal, stepFormData, serviceTypeList } = useSelector(
    (state) => state.preauthorization
  );
  const {
    viewModal: claimView,
    stepFormData: claimData,
    viewClaimInfo,
  } = useSelector((state) => state.claim);

  const selectedBranch = JSON.parse(localStorage.getItem("selectedBranch"));
  const [loading, setLoading] = useState(false);
  const [inputValue, setInputValue] = useState("");
  const [cachedOptions, setCachedOptions] = useState([]);
  const [priceListData, setPriceListData] = useState([]);
  const debounceTimeout = useRef(null);

  // Fetch price list when service type is selected
  useEffect(() => {
    if (selectedServiceType) {
      fetchPriceList();
    }
  }, [selectedServiceType]);

  const fetchPriceList = useCallback(async () => {
    try {
      setLoading(true);
      const response = await dispatch(
        GetAllServicePriceList({
          branch_id: selectedBranch?.branch_id || selectedBranch?.value || "",
          nphies_code: "",
          provider_cde: "",
          payer_id: stepFormData?.payer?.payer_id || claimData?.payer?.payer_id,
          is_activate: true,
          prdtyp: selectedServiceType,
          start_point: 0,
          limit: 100000,
        })
      ).unwrap();

      const options =
        response.data?.prdtserv_details?.map((item) => ({
          ...item,
          value: item.mediccodes_id || item.price_id, // Use mediccodes_id or price_id as unique value
          label: item.nphies_desc || item.provider_codedesc, // Use nphies_desc as label
          prodserv_code: item.nphies_code, // Map nphies_code to prodserv_code
          prodserv_desc: item.nphies_desc, // Map nphies_desc to prodserv_desc
          provider_code: item.provider_code,
          provider_codedesc: item.provider_codedesc,
          unit_price: item.unit_price,
          prodserv_unittyp: item?.prodserv_unittyp,
          prdurl: item?.prdurl,
          prodserv_pcksze: item?.prodserv_pcksze,
          prodserv_strngt: item?.prodserv_strngt,
        })) || [];

      setPriceListData(options);
    } catch (error) {
      console.error("Error fetching price list:", error);
      setPriceListData([]);
    } finally {
      setLoading(false);
    }
  }, [dispatch, selectedServiceType, selectedBranch]);

  const loadOptions = useCallback(
    (inputValue, callback) => {
      if (debounceTimeout.current) {
        clearTimeout(debounceTimeout.current);
      }

      debounceTimeout.current = setTimeout(async () => {
        try {
          setLoading(true);

          // If input is empty, return price list data
          if (!inputValue.trim() && priceListData.length > 0) {
            callback(priceListData);
            return;
          }

          // Fetch from API when user searches
          const response = await dispatch(
            GetAllServiceCodeDescription({
              branch_id:
                selectedBranch?.branch_id || selectedBranch?.value || "",
              prd_typ: selectedServiceType || "",
              prodserv_id: inputValue,
              ins_sel:
                stepFormData?.payer?.insurance_company_id ||
                claimData?.payer?.insurance_company_id ||
                "",
              claim_type:
                stepFormData?.preauthorization_type?.claim_types_id ||
                claimData?.claim_type?.claim_types_id ||
                "",
              is_prescription:
                stepFormData?.prescription || claimData?.prescription || false,
            })
          ).unwrap();

          const options =
            response.data?.Service_Code?.map((item) => ({
              ...item,
              value: item.prodserv_code,
              label: item.prodserv_desc,
            })) || [];

          setCachedOptions(options);
          callback(options);
        } catch (error) {
          console.error("Error fetching service codes:", error);
          callback([]);
        } finally {
          setLoading(false);
        }
      }, 500);
    },
    [dispatch, selectedServiceType, priceListData]
  );

  return (
    <>
      <label className="form-label">
        {label}
        {isRequired && <span className="text-danger">*</span>}
      </label>
      <Controller
        name={`${name}`}
        control={control}
        rules={{ required: isRequired }}
        render={({ field }) => (
          <AsyncSelect
            {...field}
            inputValue={inputValue}
            onInputChange={(newValue) => setInputValue(newValue)}
            isClearable
            defaultOptions={
              priceListData.length > 0 ? priceListData : cachedOptions
            }
            loadOptions={loadOptions}
            cacheOptions
            blurInputOnSelect={false}
            menuPlacement="bottom"
            size="sm"
            isLoading={loading}
            isDisabled={viewModal || claimView}
            placeholder="Search service codes..."
            classNamePrefix="react-select"
            onMenuOpen={() => {
              handleServiceMenuToggle(true);
            }}
            onMenuClose={() => handleServiceMenuToggle(false)}
            value={
              // Find matching option in price list or cached options
              priceListData.find((opt) => opt.value === field.value?.value) ||
              cachedOptions.find((opt) => opt.value === field.value?.value) ||
              watch(`${name}`)
            }
            onChange={(selected) => {
              // Only set value if user explicitly selects an option
              if (selected) {
                setValue(`${name}`, {
                  ...selected,
                  label: selected.nphies_desc || selected.prodserv_desc,
                  value: selected.value,
                });
                setValue(
                  medication ? "prescribed_medication_type" : "service_type",
                  serviceTypeList?.find(
                    (item) => item?.servtype_code === selected?.prdtyp
                  )
                );
              } else {
                setValue(`${name}`, null);
              }
              setInputValue("");
            }}
            styles={customSmStyles}
            getOptionLabel={(option) => {
              // Handle both price list and search results
              if (option.nphies_code) {
                return `${option.nphies_code} - ${option.nphies_desc}`;
              }
              return `${option.prodserv_code} - ${option.prodserv_desc}`;
            }}
            getOptionValue={(option) => option.value}
            formatOptionLabel={(option) => {
              // Extract properties from both response types
              const code = option.nphies_code || option.prodserv_code;
              const description = option.nphies_desc || option.prodserv_desc;
              const providerCode = option.provider_code;
              const providerDesc = option.provider_codedesc;
              const unitPrice = option.unit_price;
              const strength = option?.prodserv_strngt;
              const pkg = option?.prodserv_pcksze;
              const unitType = option?.prodserv_unittyp;

              return (
                <div className="d-flex justify-content-between align-items-center w-100">
                  <div className="flex-grow-1">
                    <div className="fw-bold fs-11">
                      ({code}) - {description}
                    </div>
                    {(providerCode || providerDesc) && (
                      <div className="mt-1 fs-10 text-muted">
                        {providerCode && ` ${providerCode}`}
                        {providerDesc && ` - ${providerDesc}`}
                      </div>
                    )}
                    {(strength || pkg || unitType) && (
                      <div className="mt-1 fs-10 text-muted">
                        {pkg && ` (${pkg})`} {unitType && ` - [${unitType}]`}
                        {strength && ` - ${strength}`}
                      </div>
                    )}
                  </div>
                  {unitPrice && (
                    <div className="text-success fs-11 ms-2">
                      SAR {unitPrice || 0}
                    </div>
                  )}
                </div>
              );
            }}
            noOptionsMessage={({ inputValue }) =>
              inputValue
                ? "No matching service codes found"
                : "Start typing to search service codes"
            }
          />
        )}
      />
    </>
  );
};

// Keep the same styles object as before
const customSmStyles = {
  control: (base) => ({
    ...base,
    minHeight: "29px",
    fontSize: "0.875rem",
    backgroundColor: "var(--falcon-input-bg)",
    borderColor: "var(--falcon-input-border-color)",
    "&:hover": {
      borderColor: "var(--falcon-input-focus-border-color)",
    },
  }),
  valueContainer: (base) => ({
    ...base,
    padding: "0 8px",
    color: "var(--falcon-input-color)",
  }),
  input: (base) => ({
    ...base,
    margin: 0,
    padding: 0,
    color: "var(--falcon-input-color) !important",
  }),
  indicatorsContainer: (base) => ({
    ...base,
    height: "29px",
    minHeight: "29px",
    svg: {
      color: "var(--falcon-input-color) !important",
    },
  }),
  option: (base, { isFocused, isSelected }) => ({
    ...base,
    paddingTop: 8,
    paddingBottom: 0,
    paddingLeft: 10,
    paddingRight: 10,
    fontSize: "0.8rem",
    minHeight: "30px",
    lineHeight: "1.2",
  }),
  menu: (base) => ({
    ...base,
    marginTop: 0,
    borderRadius: 0,
    backgroundColor: "var(--falcon-card-bg)",
    border: "1px solid var(--falcon-border-color)",
  }),
  menuList: (base) => ({
    ...base,
    paddingTop: 0,
    paddingBottom: 0,
  }),
  placeholder: (base) => ({
    ...base,
    color: "var(--falcon-input-placeholder-color)",
    margin: 0,
    padding: 0,
  }),
};

export default ServiceCodeSelect;
